import { observable, action, computed } from 'mobx';

import CashoutVerificationService from 'services/CashoutVerificationService';
import messagingService from 'services/MessagingService/MessagingService';
import {
  CASHOUT_STATUS_RESPONSE,
  STUDIO_PERFORMER_RESPONSE,
  STUDIO_ROSTER_RESPONSE,
} from 'services/MessagingService/EventNames';
import { CASHOUT_STATUS } from 'enum/profile';

/**
 * @class handles state of cashout status
 */
class CashoutVerificationStore {
  constructor() {
    this.messagingService = messagingService();
    this.messagingService.on(CASHOUT_STATUS_RESPONSE, this._handleCashoutResponse);
    this.messagingService.on(STUDIO_ROSTER_RESPONSE, this._handleRosterResponse);
    this.messagingService.on(STUDIO_PERFORMER_RESPONSE, this._handleStudioPerformerResponse);
  }

  destructor() {
    this.messagingService.off(CASHOUT_STATUS_RESPONSE, this._handleCashoutResponse);
    this.messagingService.off(STUDIO_ROSTER_RESPONSE, this._handleRosterResponse);
    this.messagingService.off(STUDIO_PERFORMER_RESPONSE, this._handleStudioPerformerResponse);
  }

  @observable cashoutStatus = {
    paypalStatus: CASHOUT_STATUS.NONE,
    cashOutHold: false,
    needsId: false,
  };

  @observable roster = [];
  @observable studioPerformer = {};

  /**
   * Private method for handling cashout status response
   * @param {Object} data - data from api response
   */
  @action _handleCashoutResponse = (data) => {
    this.cashoutStatus = data;
  };

  /**
   * Private method for handling roster response
   * @param {Object} data - data from api response
   */
  @action _handleRosterResponse = (data) => {
    this.roster = data;
  };

  @action _handleStudioPerformerResponse = (data) => {
    this.studioPerformer = data;
  };

  /**
   * Method for fetching cashout status
   * @param {String} username - username
   */
  getCashoutStatus = (username, headers = {}) => {
    CashoutVerificationService.getCashoutStatus(username, headers);
  };

  getCashoutStatusAsync = async(username, headers = {}) => {
    await CashoutVerificationService.getCashoutStatusAsync(username, headers);
  };

  /**
   * @method getter isBroadcastApproved - check if broadcasting is approved
   * @returns boolean
   */
  @computed get isBroadcastApproved() {
    return (
      this.cashoutStatus.paypalStatus === CASHOUT_STATUS.APPROVED_2257 ||
      this.cashoutStatus.paypalStatus === CASHOUT_STATUS.BROADCAST_APPROVED ||
      this.cashoutStatus.paypalStatus === CASHOUT_STATUS.CASH_OUT_APPROVED
    );
  }

  /* Method for fetching studio roster
   * @param {String} username - username
   */
  getStudioRoster = (username) => {
    CashoutVerificationService.getStudioRoster(username);
  };

  getStudioPerformer = (studioOwnerName, performer) => {
    CashoutVerificationService.getStudioPerformer(studioOwnerName, performer);
  };

  /* @method getter is2257Approved - check if user is 2257 approved
   * @returns boolean
   */
  @computed get is2257Approved() {
    return (
      this.cashoutStatus.paypalStatus === CASHOUT_STATUS.APPROVED_2257 ||
      this.cashoutStatus.paypalStatus === CASHOUT_STATUS.CASH_OUT_APPROVED
    );
  }

  /* @method getter noPaypalStatus - check if there is no paypal status
   * @returns boolean
   */
  @computed get noPaypalStatus() {
    return this.cashoutStatus.paypalStatus === CASHOUT_STATUS.NONE;
  }

  /* @method getter isCashoutApproved - check if user is cashout approved
   * @returns boolean
   */
  @computed get isCashoutApproved() {
    return this.cashoutStatus.paypalStatus === CASHOUT_STATUS.CASH_OUT_APPROVED;
  }

  isCashOutRestricted = (earningsDetails) =>
    this.isCashoutApproved && (this.cashoutStatus.cashOutHold || !earningsDetails.get('ableToCashOut'));
}

export default new CashoutVerificationStore();
